import { Box, Stack, Typography } from "@mui/material";
import PolicyTextLink from "../../../components/Policy/PolicyTextLink";
import { useSellerOrgConfig } from "../../../Hooks/useSellerOrgConfig";
import { hasPolicyUrl } from "../../../utils/policyLinks";

const captionSx = {
  wordBreak: "break-word",
  overflowWrap: "break-word",
  whiteSpace: "normal",
  fontSize: "clamp(10px, 1vw, 12px)",
  lineHeight: "150%",
  letterSpacing: 0,
} as const;

const footerLinkSx = {
  ...captionSx,
  fontWeight: 600,
  color: "rgba(0, 0, 0, 0.65)",
  textDecoration: "none",
  "&:hover": {
    color: "rgba(0, 0, 0, 0.85)",
    textDecoration: "underline",
  },
} as const;

const footerLabelSx = {
  ...captionSx,
  fontWeight: 600,
  color: "rgba(0, 0, 0, 0.65)",
} as const;

const RIGHTS_RESERVED_MARKER = "All rights reserved.";

const splitAuthFooterText = (text: string) => {
  const rightsIndex = text.indexOf(RIGHTS_RESERVED_MARKER);
  if (rightsIndex === -1) {
    return { mainText: text, rightsText: null as string | null };
  }

  return {
    mainText: text.slice(0, rightsIndex).trim(),
    rightsText: RIGHTS_RESERVED_MARKER,
  };
};

const Footer: React.FC<{ compactMobile?: boolean }> = ({ compactMobile = false }) => {
  const { branding } = useSellerOrgConfig();

  if (branding.authFooterText) {
    const year = new Date().getFullYear();
    const { mainText, rightsText } = splitAuthFooterText(branding.authFooterText);
    const hasLinks = Boolean(branding.authFooterLinks?.length);
    const isSplit = branding.authFooterLayout === "split" && hasLinks;
    const isCentered = !isSplit && branding.authFooterLayout !== "left";

    return (
      <Box
        component="footer"
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: hasLinks ? "center" : "flex-start", sm: "center" },
          justifyContent: isSplit ? "space-between" : isCentered ? "center" : "flex-start",
          gap: { xs: compactMobile ? 0.25 : 0.5, sm: 2 },
          width: "100%",
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: compactMobile ? 0.75 : 1, sm: 1.25 },
          height: "auto",
          minHeight: 0,
          flex: "0 0 auto",
          backgroundColor: "#FFFFFF",
          borderTop: "1px solid #f3f4f6",
        }}
      >
        {hasLinks ? (
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            divider={
              <Typography component="span" sx={{ ...captionSx, color: "rgba(0, 0, 0, 0.35)" }}>
                ·
              </Typography>
            }
            sx={{ flexShrink: 0 }}
          >
            {branding.authFooterLinks?.map((item) => (
              <PolicyTextLink
                key={item.label}
                url={item.href}
                label={item.label}
                underline="hover"
                sx={hasPolicyUrl(item.href) ? footerLinkSx : footerLabelSx}
              />
            ))}
          </Stack>
        ) : null}

        <Typography
          variant="caption"
          component="div"
          sx={{
            ...captionSx,
            width: isSplit ? { xs: "100%", sm: "auto" } : "100%",
            maxWidth: isCentered ? 720 : "none",
            textAlign: isSplit ? { xs: "center", sm: "right" } : isCentered ? "center" : "left",
            fontWeight: 500,
            color: "rgba(0, 0, 0, 0.55)",
            flex: isSplit ? { sm: 1 } : undefined,
            m: 0,
            lineHeight: compactMobile ? 1.35 : 1.5,
          }}
        >
          © {year} {mainText}
          {rightsText ? (
            <Box
              component="span"
              sx={{
                display: { xs: "block", sm: "inline" },
                mt: compactMobile ? 0.125 : 0,
              }}
            >
              <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                {" "}
              </Box>
              {rightsText}
            </Box>
          ) : null}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: { xs: "center", sm: "center" },
        justifyContent: branding.showOndcFooter
          ? { xs: "space-between", sm: "space-between" }
          : { xs: "center", sm: "center" },
        px: { xs: 1, sm: 2, md: 4 },
        height: { xs: "auto", sm: "40px" },
        backgroundColor: "#FFFFFF",
      }}
    >
      {branding.showOndcFooter && (
        <Typography variant="caption" sx={captionSx}>
          Powered By ONDC Protocol
        </Typography>
      )}
      <Typography variant="caption" sx={captionSx}>
        Valar Digital Commerce Pvt Ltd © 2025 All rights reserved
      </Typography>
    </Box>
  );
};

export default Footer;
